import { rem } from 'polished'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { MapboxMap } from 'components/Common/Mapbox/MapboxMap'
import { MapboxMarker } from 'components/Common/Mapbox/MapboxMarker'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { MapboxProvider } from 'contexts/MapboxContext'
import Tooltip from 'LuxKit/Tooltip'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import clsx from 'clsx'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import type { MapHandle, MapViewport } from 'lib/maps/types'
import { CRUISE_PILL_ICON_MARKER_TYPE } from 'constants/map'
import {
  fitCruiseMarkersOnMap,
  getCruiseItineraryMarkers,
  type CruiseItineraryMapMarker,
} from './cruiseItineraryMapUtils'

const MapContainer = styled.div`
  order: 1;

  ${mediaQueryUp.tablet} {
    order: 2;
    position: sticky;
    top: ${rem(115)};

    &.modal {
      position: relative;
      top: 0;
    }
  }

  .cruise-location-map {
    width: 100%;
    height: ${rem(450)};

    ${mediaQueryUp.tablet} {
      width: ${rem(475)};
    }
  }

  .cruise-location-map-modal {
    width: 100%;
    height: ${rem(280)};
  }
`

const Root = styled.div`
  position: relative;
  display: flex;
  pointer-events: all;
`

const StyledPill = styled.button`
  height: ${rem(24)};
  padding: 0 ${rem(8)};
  display: inline-flex;
  align-items: center;
  border-radius: ${rem(9999)};
  cursor: pointer;
  background-color: ${(props) => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(2)} solid ${(props) => props.theme.palette.neutral.surface.darkForeground};
  appearance: none;

  &.active {
    background-color: ${(props) => props.theme.palette.neutral.surface.darkBackground};
    border-color: ${(props) => props.theme.palette.neutral.surface.normalBackground};
    color: ${(props) => props.theme.palette.neutral.surface.darkContrast};
  }
`

const MAP_OPTIONS = {
  width: '100%',
  height: '100%',
}

interface Props {
  itinerary: Array<App.CruiseItineraryItem>
  portNameSelected?: string
  onSelectPort?: (portName: string | null) => void
  onMapLoaded?: (map: MapHandle) => void
  isModal?: boolean
}

function ItineraryMap({
  itinerary,
  portNameSelected,
  onSelectPort,
  onMapLoaded,
  isModal,
}: Props) {
  const intl = useIntl()
  const [map, setMap] = useState<MapHandle | null>(null)
  const markers = useMemo(() => getCruiseItineraryMarkers(itinerary, intl), [itinerary, intl])
  const initialViewport = useMemo<MapViewport>(() => ({
    latitude: markers[0]?.latitude ?? 0,
    longitude: markers[0]?.longitude ?? 0,
    zoom: markers.length > 0 ? 4 : 1,
  }), [markers])

  useEffect(() => {
    fitCruiseMarkersOnMap(map, markers)
  }, [map, markers])

  const handleMapLoad = useCallback((nextMap: MapHandle) => {
    setMap(nextMap)
    onMapLoaded?.(nextMap)
  }, [onMapLoaded])

  const setActiveMarker = useCallback((marker: CruiseItineraryMapMarker) => {
    if (portNameSelected === marker.id) {
      onSelectPort?.(null)
    } else {
      onSelectPort?.(marker.id)
    }
  }, [onSelectPort, portNameSelected])

  return (
    <MapContainer className={isModal ? 'modal' : ''}>
      <div className={isModal ? 'cruise-location-map-modal' : 'cruise-location-map'}>
        <MapboxProvider useCase={MAPBOX_ACCESS_TOKEN_USE_CASES.cruises}>
          <MapboxMap
            id="cruise-le-map"
            initialViewport={initialViewport}
            containerStyle={MAP_OPTIONS}
            loadingElement={<LoadingIndicator inline />}
            onLoad={handleMapLoad}
            showNavigationControl
            navigationControlPosition="top-right"
            dragRotate={false}
            touchPitch={false}
            minZoom={1}
            maxZoom={19}
          >
            {markers.map((marker, index) => {
              const isActive = portNameSelected === marker.id
              const zIndex = isActive ? (markers.length + 1) : (markers.length - index)

              return (
                <MapboxMarker
                  key={marker.id}
                  coordinate={{ lat: marker.latitude, lng: marker.longitude }}
                  anchor="top-left"
                  style={{ zIndex }}
                >
                  <Root>
                    <Tooltip open={isActive} description={marker.locationHeading}>
                      <StyledPill
                        type="button"
                        aria-label={marker.locationHeading}
                        onClick={() => setActiveMarker(marker)}
                        className={clsx({ active: isActive })}
                      >
                        <BodyTextBlock
                          startIcon={marker.type === CRUISE_PILL_ICON_MARKER_TYPE ? <LineShipIcon /> : undefined}
                          variant="small"
                          weight="bold"
                        >
                          {marker.name}
                        </BodyTextBlock>
                      </StyledPill>
                    </Tooltip>
                  </Root>
                </MapboxMarker>
              )
            })}
          </MapboxMap>
        </MapboxProvider>
      </div>
    </MapContainer>
  )
}

export default ItineraryMap
